import React, { ReactElement, useEffect, useId, useRef, useState } from "react";
import styled from "styled-components";

import { VoteStatus } from "types/voteEnum";

import OptionsWrapper from "./OptionsWrapper";
import ActionSection from "../../common/ActionSection";
import TitleHeader from "../../common/TitleHeader";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Divider from "@mui/material/Divider";
import {
    createInitialAnswerStatus,
    isAnswerLocked,
} from "components/vote/common/answerLock";
import {
    FRAME_INNER_BORDER_COLOR,
    onBackgroundImageSx,
} from "components/common/backgroundFrame";

type Props = {
    onValidate: (answer: string | string[]) => Promise<any>;
    subTitles: string[];
    options: { id: string; text: string }[];
    title: string;
    isAssociationQuestion?: boolean;
    canAnswerBeUpdated: boolean;
    hasCurrentUserAnswered: boolean;
};

const GroupedQuestion = ({
    onValidate,
    options,
    title,
    subTitles,
    isAssociationQuestion = false,
    canAnswerBeUpdated,
    hasCurrentUserAnswered,
}: Props) => {
    const [answers, setAnswers] = useState<(string | undefined)[]>(
        Array(subTitles.length).fill(undefined),
    );
    const [voteStatus, setVoteStatus] = useState<VoteStatus>(() =>
        createInitialVoteStatus(hasCurrentUserAnswered),
    );
    const answerIsLocked = isAnswerLocked(
        voteStatus,
        hasCurrentUserAnswered,
        canAnswerBeUpdated,
    );
    // Each sub-question's proposals are named by its sub-title
    const subTitleIdPrefix = useId();
    const [expandedAccordions, setExpandedAccordions] = useState<boolean[]>(
        Array(subTitles.length).fill(true),
    );
    const accordionHeaderRefs = useRef<Array<HTMLDivElement | null>>([]);

    useEffect(() => {
        if (answerIsLocked) {
            return;
        }
        if (
            answers.filter((a) => a !== undefined).length === subTitles.length
        ) {
            setVoteStatus(VoteStatus.PendingValidation);
        }
    }, [answers, subTitles.length, answerIsLocked]);

    const handleValidate = async () => {
        if (answerIsLocked) {
            return;
        }
        setVoteStatus(VoteStatus.Loading);
        try {
            await onValidate(answers.join(""));
            setVoteStatus(VoteStatus.Success);
        } catch {
            setVoteStatus(VoteStatus.Error);
        }
    };

    const handleReset = () => {
        setVoteStatus(VoteStatus.Idle);
        setAnswers(Array(subTitles.length).fill(undefined));
    };

    function collapseAccordion(index: number) {
        const updated = [...expandedAccordions];
        updated[index] = false;
        setExpandedAccordions(updated);
        // The chosen option is hidden by the collapse: keep the focus on the
        // item rather than letting it fall to the top of the page (RGAA 12.8)
        accordionHeaderRefs.current[index]?.focus();
    }

    const selectedAnswers = answers.filter((a) => a !== undefined) as string[];

    const renderQuestions = (): ReactElement => (
        <>
            {subTitles.map((subtitle, index) => {
                // Disabled other item in association question
                const disabledOptions = isAssociationQuestion
                    ? selectedAnswers.filter((ans) => ans !== answers[index])
                    : [];
                const closeAccordion = isAssociationQuestion
                    ? () => collapseAccordion(index)
                    : undefined;

                return (
                    <Accordion
                        key={index}
                        disableGutters
                        elevation={4}
                        // Sub-questions sit right under the question title (h1), instead of the default h3 (RGAA 9.1).
                        slotProps={{ heading: { component: "h2" } }}
                        // Inside the frame: no opaque white block nor shadow, a thin border keeps them apart.
                        sx={onBackgroundImageSx({
                            backgroundColor: "transparent",
                            boxShadow: "none",
                            border: `1px solid ${FRAME_INNER_BORDER_COLOR}`,
                        })}
                        expanded={expandedAccordions[index]}
                        onChange={(
                            event: React.SyntheticEvent,
                            isExpanded: boolean,
                        ) => {
                            const updated = [...expandedAccordions];
                            updated[index] = isExpanded;
                            setExpandedAccordions(updated);
                        }}
                    >
                        <AccordionSummary
                            ref={(element: HTMLDivElement | null) => {
                                accordionHeaderRefs.current[index] = element;
                            }}
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`panel${index}-content`}
                            id={`panel${index}-header`}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <Typography
                                    id={`${subTitleIdPrefix}-${index}`}
                                    variant="subtitle2"
                                    sx={{ fontWeight: "bold" }}
                                >
                                    {subtitle}
                                </Typography>
                                {answers[index] && (
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        {
                                            options.find(
                                                (o) => o.id === answers[index],
                                            )?.text
                                        }
                                    </Typography>
                                )}
                            </Box>
                        </AccordionSummary>
                        <Divider />
                        <AccordionDetails sx={{ pt: 0 }}>
                            <OptionsWrapper
                                titleId={`${subTitleIdPrefix}-${index}`}
                                index={index + 1}
                                options={options}
                                answers={answers}
                                setAnswers={setAnswers}
                                setVoteStatus={setVoteStatus}
                                isLocked={answerIsLocked}
                                disabledOptions={disabledOptions}
                                closeAccordion={closeAccordion}
                            />
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </>
    );

    return (
        <WrapperVote>
            <TitleHeader title={title} />
            {renderQuestions()}
            <ActionSection
                voteStatus={voteStatus}
                handleValidate={handleValidate}
                handleReset={handleReset}
                canAnswerBeUpdated={canAnswerBeUpdated}
            />
        </WrapperVote>
    );
};

const WrapperVote = styled.div`
    @media (max-width: 768px) {
        max-width: 350px;
    }
    > span {
        font-size: 1.3rem;
    }
`;

function createInitialVoteStatus(hasCurrentUserAnswered: boolean): VoteStatus {
    return createInitialAnswerStatus(hasCurrentUserAnswered);
}

export default GroupedQuestion;
